import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import Tab from '~/common/components/Tab/Tab';
import TabPanel from '~/common/components/Tab/TabPanel';
import Tabs from '~/common/components/Tab/Tabs';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import useUrl from '~/common/hooks/useUrl';
import MUIDialog from '~/common/mui/MUIDialog/MUIDialog';
import { typedApiClient } from '~/common/util/api-client';
import { useModalState } from '~/modals/modalStore';

import { LoanSummaryDatesTab } from './LoanSummaryTabs/LoanSummaryDatesTab';
import { LoanSummaryFHATab } from './LoanSummaryTabs/LoanSummaryFHATab';
import { LoanSummaryLoanTab } from './LoanSummaryTabs/LoanSummaryLoanTab';
import { LoanSummaryPropertyTab } from './LoanSummaryTabs/LoanSummaryPropertyTab';

function a11yProps(name: string, index: number) {
  return {
    id: `${name}-tab-${index}`,
    'aria-controls': `${name}-tabpanel-${index}`,
  };
}

export default function LoanSummaryDialogContainer() {
  const { isOpen } = useModalState('loanSummaryDialog');

  if (!isOpen) {
    return null;
  }

  return <LoanSummaryDialog />;
}

function LoanSummaryDialog() {
  const { isOpen, onClose } = useModalState('loanSummaryDialog');
  const [tabValue, setTabValue] = useState(0);

  const { data } = useGetLoanSummary();

  const loanInfo = data?.loanInfo;
  const propertyData = data?.propertyData;
  const miscellaneousInfo = data?.miscellaneousInfo;
  const loanSummaryDates = data?.loanSummaryDates;
  const { obfuscatedLoanIdentifier } = useUrl();
  const { data: borrowerData } = useQuery({
    queryKey: [
      'borrower',
      obfuscatedLoanIdentifier?.obfuscatedLenderDatabaseId,
      obfuscatedLoanIdentifier?.obfuscatedLoanRecordId,
    ],
    queryFn: async () => {
      const { data } =
        await typedApiClient.v1.borrower1003ValidationBorrowersList(
          obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
          obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
        );
      return data;
    },
  });
  const isFHA = loanInfo?.loanProductType === 'FHA';

  const handleTabChange = (_: unknown, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <MUIDialog
      title="Loan Summary"
      open={isOpen}
      onClose={onClose}
      anchorSelector='[data-modal-anchor="loanSummaryDialog"]'
      dialogPosition="left"
      paperSx={{
        minWidth: '49rem',
        minHeight: '39rem',
      }}
    >
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label={`Loan Summary Dialog Tabs`}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`LOAN`} {...a11yProps('loan', 0)} />
        <Tab label={`PROPERTY`} {...a11yProps('property', 1)} />
        <Tab label={`DATES`} {...a11yProps('DATES', 2)} />
        {isFHA && <Tab label={`FHA`} {...a11yProps('FHA', 3)} />}
      </Tabs>
      <TabPanel value={tabValue} index={0} name="loan">
        <LoanSummaryLoanTab
          loanInfo={loanInfo}
          propertyData={propertyData}
          miscellaneousInfo={miscellaneousInfo}
          borrowerData={borrowerData}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={1} name="property">
        <LoanSummaryPropertyTab
          loanInfo={loanInfo}
          propertyData={propertyData}
          miscellaneousInfo={miscellaneousInfo}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={2} name="DATES">
        <LoanSummaryDatesTab loanSummaryDates={loanSummaryDates} />
      </TabPanel>
      <TabPanel value={tabValue} index={3} name="FHA">
        <LoanSummaryFHATab loanInfo={loanInfo} />
      </TabPanel>
    </MUIDialog>
  );
}
